import React, {useEffect, useState} from 'react';

import {FormDefinitionContent} from 'forms/form-designer/Constants';
import {FormDesignerState, FormDesignerStateActions} from 'forms/form-designer/FormDesignerState';
import useSelectorsById, {selectorsAreLoaded} from 'notebook/structured-tables/hooks/useSelectorsById';
import {
  initializeCreateProcessModalFieldKeyToTableColumnIndex,
  initializeCreateProcessModalFormInstanceTableState,
} from 'processes/modals/CreateProcessFormInstanceState';
import {ProcessTableLocation} from 'processes/ProcessConstants';
import createEmptyTaskTableFromSchema from 'processes/tables/createEmptyTaskTableFromSchema';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';

import {FieldKeyToTableColumnIndex, FormInstanceState, FormInstanceTableState} from './Constants';
import {FormInstanceContents} from './FormInstanceContents';
import {initializeFormInstanceState} from './FormInstanceState';

/**
 * Ephemeral preview of a FormInstance that allows user input, but without a header or submit button.
 *
 * @param formDefinitionContent Form definition to generate preview from
 * @returns Interactive form instance component
 */
export default function FormInstancePreview({
  formDefinitionContent,
  formDesignerState,
}: {
  formDefinitionContent: FormDefinitionContent;
  formDesignerState: FormDesignerState;
}): JSX.Element {
  const [formInstanceState, setFormInstanceState] = useState<FormInstanceState>(
    initializeFormInstanceState(formDefinitionContent)
  );
  const [prevDefinition, setPrevDefinition] = useState<FormDefinitionContent>();
  if (formDefinitionContent !== prevDefinition) {
    // Convert FormDefinitionContent into FormInstanceFieldInputs, but keep `value` and `isValidValue` for any fields that have not changed
    setFormInstanceState(initializeFormInstanceState(formDefinitionContent, formInstanceState));
    setPrevDefinition(formDefinitionContent);
  }

  const [tableState, setTableState] = useState<FormInstanceTableState>({});
  const [fieldKeyToTableColumnIndex, setFieldKeyToTableColumnIndex] = useState<FieldKeyToTableColumnIndex>(
    {}
  );
  const selectorsById = useSelectorsById({includeArchivedSelectors: false});

  useEffect(() => {
    if (formDesignerState && selectorsAreLoaded(selectorsById)) {
      // For now, actionMap can only have 1 action
      [...formDesignerState.actionMap.values()].forEach((formDesignerAction) => {
        if (formDesignerAction.selectedSchema) {
          const table = createEmptyTaskTableFromSchema({
            name: formDesignerAction.selectedSchema.name,
            taskSchema: formDesignerAction.selectedSchema,
            tableLocation: ProcessTableLocation.CREATE_MODAL,
            locked: false,
            selectorsById,
          });
          const initializedFieldKeyToTableColumnIndex =
            initializeCreateProcessModalFieldKeyToTableColumnIndex(
              table,
              FormDesignerStateActions.generateFormActionConfigs(formDesignerState)
            );
          setFieldKeyToTableColumnIndex(initializedFieldKeyToTableColumnIndex);
          setTableState(
            initializeCreateProcessModalFormInstanceTableState(
              table,
              FormDesignerStateActions.generateFormDefinition(formDesignerState),
              initializedFieldKeyToTableColumnIndex
            )
          );
        }
      });
    }
  }, [formDesignerState, formDefinitionContent, selectorsById]);

  return (
    <div>
      <div className="u-spacing-bottom">
        <InlineBanner
          bannerType={InlineBannerType.INFO}
          text="This is a preview only."
          supportingInfo="Entering values into the form fields will not create any new data."
        />
      </div>
      <FormInstanceContents
        formDefinitionContent={formDefinitionContent}
        formInstanceTableState={tableState}
        onUpdateFormInstanceTableState={setTableState}
        fieldKeyToTableColumnIndex={fieldKeyToTableColumnIndex}
      />
    </div>
  );
}
